import { NgIf } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";

export type GemLoadingState = "loading" | "slow" | "error";

/**
 * Wraps a skeleton and escalates it: loading -> slow hint (after `slowAfterMs`)
 * -> error with retry (on `error` or after `timeoutMs`).
 */
@Component({
  selector: "gem-loading-placeholder",
  styleUrls: ["gem-loading-placeholder.component.scss"],
  template: `
    <ng-container *ngIf="state !== 'error'; else failed">
      <ng-content></ng-content>
      <p *ngIf="state === 'slow' && !compact" class="hint" role="status">{{ "loadingState.slow" | translate }}</p>
    </ng-container>
    <ng-template #failed>
      <button
        *ngIf="compact; else full"
        type="button"
        class="compact"
        [title]="('loadingState.error' | translate) + ' ' + ('loadingState.retry' | translate)"
        [attr.aria-label]="'loadingState.retry' | translate"
        (click)="onRetry()"
      >
        <i class="material-icons" aria-hidden="true">warning_amber</i>
      </button>
      <ng-template #full>
        <div class="failed" role="alert">
          <i class="material-icons" aria-hidden="true">warning_amber</i>
          <span>{{ "loadingState.error" | translate }}</span>
          <button type="button" class="btn btn-primary" (click)="onRetry()">
            {{ "loadingState.retry" | translate }}
          </button>
        </div>
      </ng-template>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, TranslateModule],
})
export class GemLoadingPlaceholderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() error = false;
  @Input() slowAfterMs = 10000;
  @Input() timeoutMs = 30000;
  /** Icon-only error state for narrow spots, e.g. the side menu. */
  @Input() compact = false;
  @Output() retry = new EventEmitter<void>();

  state: GemLoadingState = "loading";

  private timers: ReturnType<typeof setTimeout>[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.start();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["error"] && !changes["error"].firstChange) {
      this.start();
    }
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  onRetry(): void {
    this.retry.emit();
    this.start();
  }

  private start(): void {
    this.clearTimers();
    if (this.error) {
      this.state = "error";
      return;
    }
    this.state = "loading";
    this.timers = [
      setTimeout(() => this.setState("slow"), this.slowAfterMs),
      setTimeout(() => this.setState("error"), this.timeoutMs),
    ];
  }

  private setState(state: GemLoadingState): void {
    this.state = state;
    this.cdr.markForCheck();
  }

  private clearTimers(): void {
    this.timers.forEach(clearTimeout);
    this.timers = [];
  }
}
